import { Component, Input, OnChanges, OnDestroy, TemplateRef, ViewChild, input, inject } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NomenclatureType } from '@app/core/models/nomenclature.model';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { TreeViewService } from '@app/features/editeur/services/tree-view.service';
import { IfIsNotAuthorized, IfNotSuperGestionnaire } from '@app/shared/autorisations/autorisations.decorator.ts';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { descripteursContexteValidators } from '@app/shared/base-form/validators-config';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { StructureService } from '@app/shared/structure/structure.service';
import {
    Contexte,
    ContexteDetails,
    DescripteursContexte,
    DescripteursFormation,
    DescripteursGroupement,
    DescripteursGroupementContexte,
    DescripteursObjetFormation,
    DescripteursObjetFormationContexte,
    Espace,
    Groupement,
    ObjetsMaquetteService,
    PlageDeChoix,
    PointInscriptionAdministrative,
    TypeObjetMaquette
} from '@pcscol/odf-api-v1-angular';
import { PegaseDialogType, MessageContextuelComponent } from '@pcscol-portail/composants-pegase';
import { Observable, Subject, combineLatest } from 'rxjs';
import { first, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { AbstractDescripteursComponent } from '../../../../../../shared/form-models/abstract-descripteurs.component';
import { MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel, MatInput, MatError, MatHint } from '@angular/material/input';
import { FormMatErrorsDirective } from '../../../../../../shared/base-form/form-mat-errors/form-mat-errors.directive';
import { SelectNomenclatureComponent } from '../../../../../../shared/select-nomenclature/select-nomenclature/select-nomenclature.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';

interface DescripteursContexteForm {
    ects: FormControl<string | null>;
    nature: FormControl<string | null>;
}

@Component({
    selector: 'app-formulaire-descripteurs-contexte',
    templateUrl: './descripteurs-contexte.component.html',
    styleUrls: ['./descripteurs-contexte.component.scss'],
    imports: [
        MatCardTitle,
        MatCardContent,
        MatChipSet,
        MatIcon,
        MatChip,
        NgClass,
        RouterLink,
        MatIconButton,
        MatTooltip,
        MessageContextuelComponent,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        FormMatErrorsDirective,
        MatError,
        SelectNomenclatureComponent,
        MatSlideToggle,
        MatHint,
        MatButton
    ]
})
export class DescripteursContexteComponent extends AbstractDescripteursComponent implements OnChanges, OnDestroy {
    readonly espace = input.required<Espace>();
    readonly objetMaquette = input<ObjetMaquette>();
    readonly typeObjetMaquette = input.required<TypeObjetMaquette | undefined>();
    private fb = inject(NonNullableFormBuilder);
    private commandFactoryService = inject(CommandFactoryService);
    private objetsMaquetteService = inject(ObjetsMaquetteService);
    private structureService = inject(StructureService);
    private notificationService = inject(NotificationService);
    protected superGestionnaireService = inject(AutorisationsService);
    private dialogService = inject(DialogService);
    private treeview = inject(TreeViewService);

    readonly NomenclatureType = NomenclatureType;
    readonly TypesObjetMaquette = TypeObjetMaquette;
    @ViewChild('modifierDescripteursContexteValideDialogTmpl') modifierDescripteursContexteValideDialogTmpl: TemplateRef<unknown>;
    formMap = new Map<string, FormGroup<DescripteursContexteForm>>();
    plageDeChoixFormMap = new Map<
        string,
        FormGroup<{ plageDeChoix: FormControl<boolean>; min: FormControl<number | null>; max: FormControl<number | null> }>
    >();
    @Input() objetMaquetteSelected: ObjetMaquette;
    readonly idContexteActuel = input<string | null>();
    contextes: Array<ContexteDetails>;
    currentValidationStateMap = new Map<string, boolean>();
    currentPIAStateMap = new Map<string, PointInscriptionAdministrative | undefined>();
    isOpenedValeurParDefaultAlert = false;
    isAllPristine$: Observable<boolean>;

    private destroy$ = new Subject<void>();

    get etcsParDefautValue(): number | null {
        return (this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursFormation | DescripteursObjetFormation)?.ects ?? null;
    }

    get natureDefautValue(): string | null {
        if ((this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursGroupement).natureGroupement?.code) {
            return (this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursGroupement).natureGroupement!.code;
        } else if ((this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursObjetFormation).nature?.code) {
            return (this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursObjetFormation).nature!.code;
        }
        return null;
    }

    get plageDeChoixDefaultValue(): PlageDeChoix | false {
        if (!(this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursGroupement).plageDeChoix) {
            return false;
        }
        return (this.objetMaquetteSelected.descripteursObjetMaquette as DescripteursGroupement).plageDeChoix!;
    }

    ngOnChanges() {
        this.formMap.clear();
        if (this.objetMaquetteSelected.mutualise) {
            this.generateForm();
        }
        this.refreshContexte();
        this.isAllPristine$ = combineLatest(Array.from(this.formMap.entries()).map(([, f]) => f.statusChanges.pipe(startWith('')))).pipe(
            takeUntil(this.destroy$),
            map(() => {
                return Array.from(this.formMap.values()).every(f => f.pristine);
            })
        );
        this.disableFormIfNotAutorised();
    }

    private generateForm() {
        this.objetMaquetteSelected.contextes!.forEach(contexte => {
            this.formMap.set(
                contexte.id!,
                this.fb.group({
                    ects: this.fb.control<string | null>(null, { validators: descripteursContexteValidators.ects }),
                    nature: this.fb.control<string | null>(null, { validators: descripteursContexteValidators.natureObjetFormation })
                })
            );
            if (contexte.descripteursContexte) {
                this.formMap.get(contexte.id!)!.patchValue({
                    ...contexte.descripteursContexte,
                    nature: this.isContexteObjetFormation(contexte.descripteursContexte)
                        ? contexte.descripteursContexte.nature?.code
                        : (contexte.descripteursContexte as DescripteursGroupementContexte).natureGroupement?.code
                });
            }
            if (this.isGroupement) {
                this.plageDeChoixFormMap.set(
                    contexte.id!,
                    this.fb.group(
                        {
                            plageDeChoix: this.fb.control(
                                !!((this.objetMaquetteSelected as Groupement).descripteursObjetMaquette as DescripteursGroupement)
                                    .plageDeChoix
                            ),
                            min: this.fb.control<number | null>(null, { validators: descripteursContexteValidators.plageDeChoixMin }),
                            max: this.fb.control<number | null>(null, { validators: descripteursContexteValidators.plageDeChoixMax })
                        },
                        {
                            validators: [
                                control => {
                                    if (!Number.isNaN(control.value.min) !== !Number.isNaN(control.value.max)) {
                                        return { plageDeChoixDeuxValeurs: true };
                                    } else if (control.value.min > control.value.max) {
                                        return { plageDeChoixMinMax: true };
                                    } else {
                                        return null;
                                    }
                                }
                            ]
                        }
                    )
                );
                this.formMap.get(contexte.id!)?.controls.ects.disable();
                if ((contexte.descripteursContexte as DescripteursGroupementContexte).plageDeChoix) {
                    this.plageDeChoixFormMap.get(contexte.id!)?.patchValue({
                        plageDeChoix: true,
                        min: (contexte.descripteursContexte as DescripteursGroupementContexte).plageDeChoix?.min,
                        max: (contexte.descripteursContexte as DescripteursGroupementContexte).plageDeChoix?.max
                    });
                    if (contexte.valide) {
                        this.plageDeChoixFormMap.get(contexte.id!)?.controls.plageDeChoix.disable();
                    }
                }
            }

            this.currentValidationStateMap.set(contexte.id!, contexte.valide!);
            this.currentPIAStateMap.set(contexte.id!, contexte.pointInscriptionAdministrative);
            this.disableFormMapsIfNotSuperGestionnaireAndValidContext(contexte);
        });
    }

    @IfNotSuperGestionnaire()
    private disableFormMapsIfNotSuperGestionnaireAndValidContext(contexte: Contexte) {
        if (contexte.valide) {
            this.formMap.get(contexte.id!)?.disable();
            if (this.isGroupement) {
                this.plageDeChoixFormMap.get(contexte.id!)?.disable();
            }
        }
    }

    @IfIsNotAuthorized(Traitement.ODF_MODIFIER_DESCRIPTEURS_CONTEXTE)
    disableFormIfNotAutorised() {
        this.formMap.forEach(f => f.disable());
        this.plageDeChoixFormMap.forEach(f => f.disable());
    }

    refreshContexte() {
        this.objetsMaquetteService
            .listerContextes(this.structureService.current$.value, this.objetMaquetteSelected.id!)
            .pipe(first(), takeUntil(this.destroy$))
            .subscribe(contextes => {
                this.contextes = contextes.sort(a => {
                    return a.id === this.idContexteActuel() ? -1 : 1;
                });
                this.contextes = this.sortContextes(contextes);
            });
    }

    modifierDescripteursContexteHandler(contexte: ContexteDetails): void {
        if (contexte.valide) {
            this.modifierDescripteursContextuelsSurBrancheValideeWithConfirmationDialog(contexte);
        } else {
            this.modifierDescripteursContexte(contexte);
        }
    }

    private modifierDescripteursContexte(contexte: ContexteDetails) {
        const oldDescripteursContexte = this.objetMaquetteSelected.contextes!.find(ctx => ctx.id === contexte.id)!.descripteursContexte!;
        const { plageDeChoix, min, max } = this.plageDeChoixFormMap.get(contexte.id!)?.getRawValue() ?? {};
        this.commandFactoryService
            .modifierDescripteursContexteObjetMaquetteCommand(
                this.objetMaquetteSelected.id!,
                this.objetMaquetteSelected.version,
                contexte.id!,
                oldDescripteursContexte,
                {
                    typeObjetMaquette: oldDescripteursContexte.typeObjetMaquette,
                    ...this.formMap.get(contexte.id!)!.value,
                    ...(this.isGroupement ? { plageDeChoix: plageDeChoix && max ? { min, max } : undefined } : {}),
                    ...(this.isGroupement ? { natureGroupement: this.formMap.get(contexte.id!)!.value.nature ?? undefined } : {})
                }
            )
            .do()
            .pipe(
                first(),
                takeUntil(this.destroy$),
                tap(om => (this.objetMaquetteSelected = om))
            )
            .subscribe(() => {
                this.notificationService.success('Le contexte a été enregistré.');
                this.formMap.get(contexte.id!)?.markAsPristine();
                this.formMap.get(contexte.id!)?.updateValueAndValidity();
                this.plageDeChoixFormMap.get(contexte.id!)?.markAsPristine();
                this.plageDeChoixFormMap.get(contexte.id!)?.updateValueAndValidity();
                this.treeview.refresh();
            });
    }
    private modifierDescripteursContextuelsSurBrancheValideeWithConfirmationDialog(contexte: ContexteDetails) {
        this.dialogService
            .openConfirmationDialog({
                titre: "Modification d'un contexte validé",
                template: this.modifierDescripteursContexteValideDialogTmpl,
                type: PegaseDialogType.WARN
            })
            .subscribe((confirmation?: boolean) => {
                if (confirmation) this.modifierDescripteursContexte(contexte);
            });
    }

    private isContexteObjetFormation(contexte: DescripteursContexte): contexte is DescripteursObjetFormationContexte {
        return contexte.typeObjetMaquette === TypeObjetMaquette.ObjetFormation;
    }

    canSaveContext(contextId: string): boolean {
        return (
            (!!this.formMap.get(contextId)?.dirty || !!this.plageDeChoixFormMap.get(contextId)?.dirty) &&
            !!this.formMap.get(contextId)?.valid &&
            !this.plageDeChoixFormMap.get(contextId)?.invalid
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    sortContextes(contextesIn: ContexteDetails[]): ContexteDetails[] {
        return contextesIn.sort((contexteA, contexteB) => {
            for (let index = 0; index < contexteA.cheminCodesTemoinValideList!.length - 1; index++) {
                if (
                    contexteA.cheminCodesTemoinValideList![index].codeObjetMaquette !==
                    contexteB.cheminCodesTemoinValideList![index].codeObjetMaquette
                ) {
                    return (
                        contexteA.cheminCodesTemoinValideList![index].codeObjetMaquette?.localeCompare(
                            contexteB.cheminCodesTemoinValideList![index].codeObjetMaquette ?? ''
                        ) ?? -1
                    );
                }
            }
            return -1;
        });
    }
}
